/**
 * Loading placeholders shaped like what's coming, so the page doesn't jump when the data arrives and it's
 * clear what will be where. Build a page's skeleton from these pieces, matching its real layout:
 *
 *   <SkCard rows={4} />             a card: header (icon, title, subtitle) and list rows
 *   <SkStatTiles n={4} />           a row of stat tiles
 *   <SkTable rows={6} cols={4} />   a table card
 *   <SkCardGrid n={4} />            a grid of cards
 *
 * The plain <Skeleton className="h-[320px]" /> from ui.tsx draws an SkCard when it is tall enough.
 */
import type { CSSProperties, ReactNode } from "react";

const cx = (...p: (string | false | null | undefined)[]) => p.filter(Boolean).join(" ");

/** One shimmering bar or shape. */
export function Sk({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span aria-hidden className={cx("rb-skeleton block", className)} style={style} />;
}

/** Visually hidden "Loading…" for screen readers, once per skeleton. */
function Busy({ children }: { children: ReactNode }) {
  return <div role="status" aria-live="polite" aria-busy="true" className="contents">{children}<span className="sr-only">Loading…</span></div>;
}

function Row({ i, avatar = true, trailing = true }: { i: number; avatar?: boolean; trailing?: boolean }) {
  const w = ["w-3/5", "w-2/5", "w-1/2", "w-2/3", "w-[45%]"][i % 5];
  return (
    <div className="flex items-center gap-3 px-5 py-3">
      {avatar && <Sk className="h-9 w-9 flex-shrink-0 rounded-xl" />}
      <div className="min-w-0 flex-1 space-y-2">
        <Sk className={cx("h-3 rounded-full", w)} />
        <Sk className="h-2.5 w-1/3 rounded-full opacity-70" />
      </div>
      {trailing && <Sk className="h-6 w-14 flex-shrink-0 rounded-lg" />}
    </div>
  );
}

/** A card: header and `rows` list rows. With `height`, rows fill that height (extra ones are clipped). */
export function SkCard({ rows = 4, className, avatar = true, body }: { rows?: number; className?: string; avatar?: boolean; body?: ReactNode }) {
  return (
    <Busy>
      <div className={cx("rb-card flex min-w-0 flex-col overflow-hidden", className)}>
        <div className="flex items-center gap-3 border-b border-rb-line px-5 py-4">
          <Sk className="h-8 w-8 flex-shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1 space-y-2">
            <Sk className="h-3.5 w-40 max-w-[60%] rounded-full" />
            <Sk className="h-2.5 w-64 max-w-[80%] rounded-full opacity-70" />
          </div>
        </div>
        {body ?? <div className="min-h-0 flex-1 divide-y divide-rb-line/70 overflow-hidden">{Array.from({ length: rows }, (_, i) => <Row key={i} i={i} avatar={avatar} />)}</div>}
      </div>
    </Busy>
  );
}

/** A row of stat tiles (number + label), as on most pages under the page band. */
export function SkStatTiles({ n = 4, className, dark }: { n?: number; className?: string; dark?: boolean }) {
  return (
    <div className={cx("grid grid-cols-2 gap-3", n >= 4 ? "lg:grid-cols-4" : n === 3 ? "lg:grid-cols-3" : "", className)} aria-hidden>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className={cx("rounded-2xl p-4", dark ? "bg-white/[0.07] ring-1 ring-white/10" : "rb-card")}>
          <Sk className={cx("h-2.5 w-20 rounded-full", dark && "opacity-30")} />
          <Sk className={cx("mt-3 h-6 w-16 rounded-lg", dark && "opacity-30")} />
          <Sk className={cx("mt-2.5 h-2 w-24 rounded-full", dark ? "opacity-20" : "opacity-60")} />
        </div>
      ))}
    </div>
  );
}

/** A table in a card: header row and `rows` rows of `cols` cells. */
export function SkTable({ rows = 6, cols = 4, className }: { rows?: number; cols?: number; className?: string }) {
  return (
    <Busy>
      <div className={cx("rb-card overflow-hidden", className)}>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <Sk className="h-3.5 w-36 rounded-full" />
          <Sk className="h-8 w-28 rounded-lg" />
        </div>
        <div className="grid gap-4 border-y border-rb-line bg-rb-surface-2/60 px-5 py-2.5" style={{ gridTemplateColumns: `2fr repeat(${cols - 1}, 1fr)` }}>
          {Array.from({ length: cols }, (_, i) => <Sk key={i} className="h-2.5 w-16 rounded-full opacity-70" />)}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="grid items-center gap-4 border-b border-rb-line/70 px-5 py-3 last:border-0" style={{ gridTemplateColumns: `2fr repeat(${cols - 1}, 1fr)` }}>
            <Sk className={cx("h-3 rounded-full", ["w-4/5", "w-3/5", "w-2/3", "w-1/2"][r % 4])} />
            {Array.from({ length: cols - 1 }, (_, c) => <Sk key={c} className="h-3 w-12 rounded-full" />)}
          </div>
        ))}
      </div>
    </Busy>
  );
}

/** A grid of `n` cards, each a header plus a few lines and a footer. */
export function SkCardGrid({ n = 4, className, cols = "sm:grid-cols-2" }: { n?: number; className?: string; cols?: string }) {
  return (
    <Busy>
      <div className={cx("grid grid-cols-1 gap-4", cols, className)}>
        {Array.from({ length: n }, (_, i) => (
          <div key={i} className="rb-card p-5">
            <div className="flex items-start gap-3">
              <Sk className="h-10 w-10 flex-shrink-0 rounded-xl" />
              <div className="min-w-0 flex-1 space-y-2">
                <Sk className="h-3.5 w-1/2 rounded-full" />
                <Sk className="h-2.5 w-1/3 rounded-full opacity-70" />
              </div>
              <Sk className="h-11 w-11 flex-shrink-0 rounded-full" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[0, 1, 2, 3].map((k) => <Sk key={k} className="h-12 rounded-xl opacity-80" />)}
            </div>
            <div className="mt-4 flex gap-2">
              <Sk className="h-8 w-28 rounded-lg" />
              <Sk className="h-8 w-24 rounded-lg opacity-70" />
            </div>
          </div>
        ))}
      </div>
    </Busy>
  );
}

/** A form card: label + field pairs and a button. */
export function SkForm({ fields = 3, className }: { fields?: number; className?: string }) {
  return (
    <SkCard className={className} body={
      <div className="space-y-4 p-5">
        {Array.from({ length: fields }, (_, i) => (
          <div key={i} className="space-y-2">
            <Sk className="h-2.5 w-24 rounded-full opacity-70" />
            <Sk className="h-11 w-full rounded-xl" />
          </div>
        ))}
        <Sk className="h-10 w-36 rounded-xl" />
      </div>
    } />
  );
}
